import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Badge, Button, Card, Col, Form, Modal, ProgressBar, Row, Spinner,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExport, faGavel, faLink, faMoneyBillTransfer, faRotateLeft, faTriangleExclamation, faTruckFast, faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons";
import { toast } from "react-toastify";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import { usePaypalJobs } from "../../contexts/PaypalJobsContext";

const SHEET_GROUP = "tiktok_lising_sheets";
const STORAGE_KEY = "paypal_tools_selected_sheet";

// Map a UI "kind" to the server-side job type. Used for both submit and
// progress lookup so the inline cards keep showing the latest run state.
const KIND_TO_TYPE = {
  payments: "EXPORT_PAYMENTS",
  refunds: "EXPORT_REFUNDS",
  tracking: "EXPORT_TRACKING",
  addTracking: "ADD_TRACKING",
  disputes: "EXPORT_DISPUTES",
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const monthsAgoIso = (m) => {
  const d = new Date();
  d.setMonth(d.getMonth() - m);
  return d.toISOString().slice(0, 10);
};

// Read the linked PayPal account ids stored on the sheet option entry.
// Tolerates both the canonical `values.paypalAccountIds` array and missing data.
const getSheetLinkedAccountIds = (sheet) => {
  if (!sheet) return [];
  const ids = sheet.values?.paypalAccountIds;
  return Array.isArray(ids) ? ids.filter(Boolean) : [];
};

const PaypalTools = ({ accountList }) => {
  const { lastJobByType, submitJob, cancelJob } = usePaypalJobs();

  const [sheetsList, setSheetsList] = useState([]);
  const [selectedSheetId, setSelectedSheetId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  const [loadingSheets, setLoadingSheets] = useState(true);

  const [startDate, setStartDate] = useState(monthsAgoIso(1));
  const [endDate, setEndDate] = useState(todayIso());

  // Modal state for managing the PayPal account ↔ sheet link.
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState([]);
  const [savingLink, setSavingLink] = useState(false);

  // Per-kind active job lookup. A job is "active" while queued or running.
  const jobByKind = useMemo(() => {
    const out = {};
    Object.entries(KIND_TO_TYPE).forEach(([kind, type]) => {
      out[kind] = lastJobByType[type] || null;
    });
    return out;
  }, [lastJobByType]);

  const isKindActive = (kind) => {
    const j = jobByKind[kind];
    return j && (j.status === "queued" || j.status === "running");
  };
  const anyActive = Object.keys(KIND_TO_TYPE).some(isKindActive);

  useEffect(() => {
    const fetchSheets = async () => {
      try {
        const response = await Utils._apiRequest({
          path: Enums.endpoints.options.LIST,
          type: "GET",
        });
        const options = response.data || [];
        const list = options.filter((o) => o.group === SHEET_GROUP);
        setSheetsList(list);
        if (selectedSheetId && !list.some((s) => s.key === selectedSheetId)) {
          setSelectedSheetId("");
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.log("fetch sheets:", e.message);
      } finally {
        setLoadingSheets(false);
      }
    };
    fetchSheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accountById = useMemo(() => {
    const map = {};
    (accountList || []).forEach((a) => { map[a._id] = a; });
    return map;
  }, [accountList]);
  const accountLabel = (accId) => {
    const a = accountById[accId];
    return a ? `${a.accountName} (${a.meta?.mode || 'live'})` : accId;
  };

  const selectedSheet = useMemo(
    () => sheetsList.find((s) => s.key === selectedSheetId) || null,
    [sheetsList, selectedSheetId],
  );
  // Linked accounts from the selected sheet, filtered to ones that still exist
  // in the current account list (so deletions don't leak stale ids into jobs).
  const linkedAccountIds = useMemo(() => {
    const ids = getSheetLinkedAccountIds(selectedSheet);
    if (!accountList?.length) return ids;
    const valid = new Set(accountList.map((a) => a._id));
    return ids.filter((id) => valid.has(id));
  }, [selectedSheet, accountList]);

  const handleSheetChange = (e) => {
    const value = e.target.value;
    setSelectedSheetId(value);
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  };

  /* -------------------- Link modal handlers -------------------- */
  const openLinkModal = () => {
    if (!selectedSheet) {
      toast.warning("Chọn Google Sheet trước");
      return;
    }
    setLinkDraft(getSheetLinkedAccountIds(selectedSheet));
    setLinkModalOpen(true);
  };
  const toggleLinkDraft = (id) => {
    setLinkDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleLinkDraftAll = () => {
    if (!accountList?.length) return;
    if (linkDraft.length === accountList.length) setLinkDraft([]);
    else setLinkDraft(accountList.map((a) => a._id));
  };
  const saveLink = async () => {
    if (!selectedSheet) return;
    setSavingLink(true);
    try {
      // Preserve all existing fields on `values`; only update paypalAccountIds.
      const nextValues = {
        ...(selectedSheet.values || {}),
        paypalAccountIds: linkDraft,
      };
      const payload = {
        key: selectedSheet.key,
        group: SHEET_GROUP,
        values: nextValues,
      };
      const updated = await Utils._apiRequest({
        path: Enums.endpoints.options.SAVE,
        type: "POST",
        payload,
      });
      setSheetsList((prev) => prev.map((s) => (s.key === selectedSheet.key ? (updated || { ...s, values: nextValues }) : s)));
      toast.success("Đã cập nhật liên kết tài khoản PayPal");
      setLinkModalOpen(false);
    } catch (err) {
      toast.error(err?.message || "Không lưu được liên kết");
    } finally {
      setSavingLink(false);
    }
  };

  const validateInputs = (forSheet = true) => {
    if (!selectedSheetId) { toast.warning("Chọn Google Sheet để lưu kết quả"); return false; }
    if (!linkedAccountIds.length) {
      toast.warning("Sheet chưa liên kết tài khoản PayPal — bấm 'Liên kết tài khoản'");
      return false;
    }
    if (forSheet && (!startDate || !endDate)) { toast.warning("Chọn khoảng ngày"); return false; }
    return true;
  };

  /**
   * Helper: enqueue a background job. The inline progress card below the
   * action button (and the Jobs panel at the bottom of the page) reflect the
   * worker state as it reports progress; we deliberately do NOT toast about
   * the queue itself — the user wants to see real task progress, not
   * scheduling chatter.
   */
  const enqueue = async (kind, payloadFactory, label, requireDateRange = true) => {
    if (!validateInputs(requireDateRange)) return;
    if (isKindActive(kind)) {
      toast.info(`${label}: đã có job đang chạy`);
      return;
    }
    try {
      await submitJob(KIND_TO_TYPE[kind], payloadFactory(), label);
    } catch (err) {
      // submitJob already toasts on failure; nothing else to do.
    }
  };

  /* -------------------- PAYMENTS -------------------- */
  const runPayments = () => enqueue(
    "payments",
    () => ({ accountIds: linkedAccountIds, sheetId: selectedSheetId, sheetName: "Payments", startDate, endDate }),
    "Export Payments",
  );

  /* -------------------- REFUNDS -------------------- */
  const runRefunds = () => enqueue(
    "refunds",
    () => ({ accountIds: linkedAccountIds, sheetId: selectedSheetId, sheetName: "Payments", startDate, endDate }),
    "Export Refunds",
  );

  /* -------------------- TRACKING (auto, account detected per row) -------------------- */
  const runTracking = () => enqueue(
    "tracking",
    () => ({ sheetId: selectedSheetId, sheetName: "Payments" }),
    "Export Tracking",
    false,
  );

  /* -------------------- ADD TRACKING -------------------- */
  const runAddTracking = () => enqueue(
    "addTracking",
    () => ({ sheetId: selectedSheetId, sheetName: "Payments" }),
    "Add Tracking",
    false,
  );

  /* -------------------- EXPORT DISPUTES (DB → Sheet) -------------------- */
  // Disputes are pulled from the local Mongo cache (populated by Sync
  // Disputes), filtered to the linked accounts so each sheet only sees its
  // own disputes. No date range — Sync already controls the lookback.
  const runExportDisputes = () => enqueue(
    "disputes",
    () => ({ accountIds: linkedAccountIds, sheetId: selectedSheetId, sheetName: "Disputes" }),
    "Export Disputes",
    false,
  );

  /* -------------------- Render helpers -------------------- */
  const renderProgress = (kind) => {
    const j = jobByKind[kind];
    if (!j) return null;

    const status = j.status;

    // Queued: no real task progress yet — show a minimal "starting" hint
    // instead of queue plumbing details. Cancel button still available.
    if (status === "queued") {
      return (
        <div className="mt-2 small d-flex align-items-center gap-2 text-muted">
          <Spinner size="sm" animation="border" variant="secondary" />
          <span>Đang khởi động…</span>
          <Button
            size="sm"
            variant="link"
            className="p-0 ms-auto text-danger small"
            onClick={() => cancelJob(j._id).then(() => toast.info("Đã yêu cầu hủy")).catch((e) => toast.error(e.message))}
          >
            <FontAwesomeIcon icon={faXmark} className="me-1" />Hủy
          </Button>
        </div>
      );
    }

    const variant = status === "error" ? "danger"
      : status === "done" ? "success"
      : status === "cancelled" ? "warning"
      : "info";
    const total = j.progress?.total || 0;
    const current = j.progress?.current || 0;
    const pct = total > 0
      ? Math.round((current / total) * 100)
      : (status === "running" ? 100 : (status === "done" ? 100 : 0));
    const msg = j.error || j.progress?.message || "";

    return (
      <div className="mt-2">
        <ProgressBar
          now={pct}
          striped={status === "running"}
          animated={status === "running"}
          variant={variant}
          label={total > 0 ? `${current}/${total}` : ""}
          style={{ height: 14 }}
        />
        <div className="small mt-1 d-flex align-items-center gap-2">
          {status === "running" && <Badge bg="primary">Đang chạy</Badge>}
          {status === "done" && <Badge bg="success">Hoàn tất</Badge>}
          {status === "error" && <Badge bg="danger">Lỗi</Badge>}
          {status === "cancelled" && <Badge bg="warning">Đã hủy</Badge>}
          <span className={status === "error" ? "text-danger" : "text-muted"}>{msg}</span>
          {status === "running" && (
            <Button
              size="sm"
              variant="link"
              className="p-0 ms-auto text-danger small"
              onClick={() => cancelJob(j._id).then(() => toast.info("Đã yêu cầu hủy")).catch((e) => toast.error(e.message))}
            >
              <FontAwesomeIcon icon={faXmark} className="me-1" />Hủy
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderRunButton = (kind, label, variant, onClick) => {
    const active = isKindActive(kind);
    return (
      <Button size="sm" variant={variant} disabled={active} onClick={onClick}>
        {active ? (
          <><Spinner size="sm" animation="border" /> Đang chạy…</>
        ) : label}
      </Button>
    );
  };

  return (
    <div className="my-3">
      <Card className="mb-3">
        <Card.Header className="d-flex align-items-center">
          <FontAwesomeIcon icon={faFileExport} className="me-2" />
          <strong>Công cụ Export</strong>
        </Card.Header>
        <Card.Body>
          {accountList?.length === 0 && (
            <Alert variant="warning" className="py-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
              Chưa có tài khoản PayPal nào. Hãy thêm tài khoản ở phía trên trước.
            </Alert>
          )}

          <Row className="g-3">
            <Col md={9}>
              <Form.Group>
                <Form.Label className="small">Google Sheet lưu kết quả</Form.Label>
                <Form.Select
                  size="sm"
                  value={selectedSheetId}
                  onChange={handleSheetChange}
                  disabled={loadingSheets || anyActive}
                >
                  <option value="">-- Chọn sheet đã lưu --</option>
                  {sheetsList.map((s) => {
                    const linked = getSheetLinkedAccountIds(s).length;
                    const name = s.values?.name || s.key;
                    return (
                      <option key={s.key} value={s.key}>
                        {name}{linked ? `  •  ${linked} tài khoản` : "  •  chưa liên kết"}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button
                as={Link}
                to="/settings#google-sheet"
                variant="dark"
                size="sm"
                className="w-100"
              >
                <FontAwesomeIcon icon={faGoogleDrive} className="me-2" />
                Quản lý Sheets
              </Button>
            </Col>

            <Col md={12}>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="small text-muted">
                  Tài khoản PayPal liên kết với sheet ({linkedAccountIds.length}):
                </span>
                {selectedSheet && linkedAccountIds.length === 0 && (
                  <Badge bg="warning" text="dark">Chưa liên kết tài khoản nào</Badge>
                )}
                {linkedAccountIds.map((id) => (
                  <Badge key={id} bg="info" className="text-white">{accountLabel(id)}</Badge>
                ))}
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="ms-auto"
                  disabled={!selectedSheet || anyActive || !accountList?.length}
                  onClick={openLinkModal}
                >
                  <FontAwesomeIcon icon={faLink} className="me-1" />
                  Liên kết tài khoản
                </Button>
              </div>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Từ ngày</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={anyActive}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Đến ngày</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={anyActive}
                />
              </Form.Group>
            </Col>
            <Col md={6} className="small text-muted d-flex align-items-end">
              <em>Khoảng ngày chỉ áp dụng cho Export Payments &amp; Export Refunds.</em>
            </Col>
          </Row>

          <hr />

          <Row className="g-3">
            <Col md={6} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faMoneyBillTransfer} className="me-2 text-primary" />
                    Export Payments
                  </h6>
                  <p className="small text-muted mb-2">
                    Lấy giao dịch nhận tiền (T0006) từ PayPal, ghi vào tab <code>Payments</code>.
                  </p>
                  {renderRunButton("payments", "Export Payments", "primary", runPayments)}
                  {renderProgress("payments")}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faRotateLeft} className="me-2 text-danger" />
                    Export Refunds
                  </h6>
                  <p className="small text-muted mb-2">
                    Lấy refund (gồm cả chargeback) và cập nhật vào cột <code>Refunded</code> của sheet <code>Payments</code> theo <code>Invoice</code>.
                  </p>
                  {renderRunButton("refunds", "Export Refunds", "danger", runRefunds)}
                  {renderProgress("refunds")}
                </Card.Body>
              </Card>
            </Col>
            <Col md={12} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faTruckFast} className="me-2 text-info" />
                    Shipping Tracker
                    <Badge bg="info" className="ms-2">Auto</Badge>
                  </h6>
                  <p className="small text-muted mb-2">
                    <strong>Export</strong>: kéo tracking từ PayPal về sheet <code>Payments</code>.<br />
                    <strong>Add</strong>: đẩy tracking nhập tay từ sheet lên PayPal.
                  </p>
                  <Alert variant="info" className="py-1 px-2 small mb-2">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
                    Tự xác định account theo cột <code>Account ID</code>. Dòng thiếu cột này sẽ bị bỏ qua — chạy <em>Export Payments</em> trước để bổ sung.
                  </Alert>
                  <div className="d-flex gap-2 flex-wrap">
                    {renderRunButton("tracking", "Export Tracking", "info", runTracking)}
                    {renderRunButton("addTracking", "Add Tracking", "success", runAddTracking)}
                  </div>
                  {renderProgress("tracking")}
                  {renderProgress("addTracking")}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faGavel} className="me-2 text-warning" />
                    Export Disputes
                  </h6>
                  <p className="small text-muted mb-2">
                    Đẩy danh sách disputes đã sync từ DB ra tab <code>Disputes</code> của sheet (append mới + update theo <code>Transaction ID</code>).
                  </p>
                  <Alert variant="info" className="py-1 px-2 small mb-2">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
                    Cần chạy <em>Sync Disputes</em> trước (ở box phía dưới) để có dữ liệu trong DB.
                  </Alert>
                  {renderRunButton("disputes", "Export Disputes", "warning", runExportDisputes)}
                  {renderProgress("disputes")}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Modal: liên kết tài khoản PayPal với sheet đang chọn */}
      <Modal show={linkModalOpen} onHide={() => !savingLink && setLinkModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">
            <FontAwesomeIcon icon={faLink} className="me-2" />
            Liên kết tài khoản PayPal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSheet && (
            <div className="small text-muted mb-2">
              Sheet: <strong>{selectedSheet.values?.name || selectedSheet.key}</strong>
            </div>
          )}
          {!accountList?.length && (
            <Alert variant="warning" className="py-2 small mb-2">
              Chưa có tài khoản PayPal. Hãy thêm tài khoản trước.
            </Alert>
          )}
          {!!accountList?.length && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small text-muted">
                  Đã chọn {linkDraft.length}/{accountList.length}
                </span>
                <Button variant="link" size="sm" className="p-0 small" onClick={toggleLinkDraftAll}>
                  {linkDraft.length === accountList.length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
                </Button>
              </div>
              <div
                className="border rounded p-2"
                style={{ maxHeight: 280, overflowY: "auto", background: "#fff" }}
              >
                {accountList.map((a) => (
                  <Form.Check
                    key={a._id}
                    type="checkbox"
                    id={`link-acc-${a._id}`}
                    className="small"
                    checked={linkDraft.includes(a._id)}
                    onChange={() => toggleLinkDraft(a._id)}
                    label={`${a.accountName} (${a.meta?.mode || "live"})`}
                  />
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" disabled={savingLink} onClick={() => setLinkModalOpen(false)}>
            Hủy
          </Button>
          <Button variant="primary" size="sm" disabled={savingLink || !selectedSheet} onClick={saveLink}>
            {savingLink ? (<><Spinner size="sm" animation="border" /> Đang lưu…</>) : "Lưu liên kết"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PaypalTools;
